import {FormControl, FormField} from 'platform/components';

import {UseFormReturn, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';
import {TITLE_EXTENSION_STORAGE_MAX_LENGTH} from '@dms/teas';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useTitleExtensionGenerateAction} from '../hooks/useTitleExtensionGenerateAction';
import {WindshieldSettingsFormValues} from '../types/WindshieldSettingsFormValues';

interface WindshieldTitleExtensionFieldProps extends TestIdProps {
  control: FormControl<WindshieldSettingsFormValues>;
  formApi: UseFormReturn<WindshieldSettingsFormValues>;
  vehicleId: string;
  isDisabled: boolean;
}

/**
 * The windshield sheet's title extension, with the same "Generate from equipment" action as
 * the Ad platforms tab. It is a printed document with no language variants, so the language
 * is omitted and the suggestion resolves to the tenant's default content language.
 */
export function WindshieldTitleExtensionField(props: WindshieldTitleExtensionFieldProps) {
  // Read-only subscription — the FormField below owns the controller registration.
  const watchedValue = useWatch({name: 'title', control: props.control});
  const fieldValue = typeof watchedValue === 'string' ? watchedValue : '';

  const generateActions = useTitleExtensionGenerateAction({
    vehicleId: props.vehicleId,
    getCurrentValue: () => props.formApi.getValues('title') ?? '',
    isDisabled: props.isDisabled,
    onGenerated: (text) => props.formApi.setValue('title', text, {shouldDirty: true}),
  });

  return (
    <FormField
      control={props.control}
      label={i18n.t('page.advertisement.labels.titleExtension')}
      name="title"
      type="text"
      isDisabled={props.isDisabled}
      tooltip={i18n.t('page.advertisement.labels.titleExtensionTooltip')}
      maxLength={TITLE_EXTENSION_STORAGE_MAX_LENGTH}
      // Counter inside the input rather than via `isCounterVisible`: the label row now
      // carries the Generate action, and Label's space-between would strand the counter
      // between the label and that action. Non-breaking spaces because TextInput's suffix
      // does not set noWrap, so plain spaces let it break onto a second line.
      suffix={`${fieldValue.length} / ${TITLE_EXTENSION_STORAGE_MAX_LENGTH}`}
      labelActions={generateActions}
      data-testid={suffixTestId('title', props)}
    />
  );
}
